import { useContext, useMemo } from "react";

import { AuthContext, type AuthContextValue } from "./context";
import type { Permission, Scope } from "./permissions";

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return context;
}

export interface PermissionApi {
  /** Does the signed-in user hold this permission? */
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  canAll: (...permissions: Permission[]) => boolean;
  /** The row-visibility scope attached to a permission, if held. */
  scopeFor: (permission: Permission) => Scope | undefined;
  hasRole: (role: string) => boolean;
}

/**
 * Permission checks for the UI.
 *
 * **These are UX, not security.** Their only job is to avoid showing a control
 * that would 403. Every gated action is independently enforced server-side by
 * the route guard, the scope-aware repository and row-level security. Never
 * treat a passing check here as authorisation.
 * docs/07-frontend.md §6
 */
export function usePermissions(): PermissionApi {
  const { session } = useAuth();

  return useMemo<PermissionApi>(() => {
    const held = new Set<string>(session?.permissions ?? []);
    const roles = new Set<string>(session?.roles ?? []);
    const scopes = session?.scopes ?? {};

    return {
      can: (permission) => held.has(permission),
      canAny: (...permissions) => permissions.some((p) => held.has(p)),
      canAll: (...permissions) => permissions.every((p) => held.has(p)),
      scopeFor: (permission) => scopes[permission],
      hasRole: (role) => roles.has(role),
    };
  }, [session]);
}

/** The school's own timezone and locale — never the browser's.
 *
 * An attendance date must not shift because a parent is travelling.
 * docs/07-frontend.md §10 */
export function useTenantFormatting() {
  const { session } = useAuth();
  const timeZone = session?.tenant.timezone ?? "UTC";
  const locale = session?.tenant.locale ?? "en-IN";
  const currency = session?.tenant.currency ?? "INR";

  return useMemo(
    () => ({
      timeZone,
      locale,
      formatDate: (value: string | Date) =>
        new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone }).format(
          typeof value === "string" ? new Date(value) : value,
        ),
      formatDateTime: (value: string | Date) =>
        new Intl.DateTimeFormat(locale, {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone,
        }).format(typeof value === "string" ? new Date(value) : value),
      formatMoney: (amount: number) =>
        new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount),
    }),
    [locale, timeZone, currency],
  );
}
